import {
  ColumnSourceEnum,
  DetailViewMappingIN,
  DynamicColumnOUT,
  MappingEditorApiV1RoutersIndexFilterPropertyBrowserBuildingViewNameEnum,
  MaterialResourceOUT,
  ObjectOperationIN,
  ObjectUpdateData,
  PropertyBrowserTreeNode,
} from "../../api-client/generated/api";
import { Row } from "@tanstack/react-table";
import { makeAutoObservable } from "mobx";
import {
  FileOperationIN,
  Filtering,
  ObjectListIN,
  ObjectListOUT,
  OperationType,
  SingleItem,
  calculationsApi,
  dynamicMappingEditorFp,
  fileOperationsApi,
  getLocalAuthHeader,
  inventoryMappingEditorAPI,
  mappingEditorDynamicColumnsFp,
  mappingEditorObjectsApi,
  objectOperationsApi,
} from "../../api-client";
import {
  FileMappingStatusType,
  ObjectDetails,
  LastDraggedObject,
  FileMappingInProgress,
  OutdatedComponents,
  OpenEditObjectPopup,
  OpenProductDetailsPopup,
} from "./types";
import { detailViewV2Store } from "./features/ObjectDetailsSlideOver/DetailViewStore";
import { AxiosError, AxiosResponse } from "axios";
import { NotificationType } from "components/Toast";
import { bimViewerStore } from "store/BimViewerStore";
import { filterMEStore } from "../DynamicTable/features/Filters/FiltersStore.ts";
import { dynamicTableStore } from "../DynamicTable/DynamicTableStore";
import { mappingSnapshotStore } from "./features/MappingSnapshotUndo/MappingSnapshotStore.ts";

class MappingEditorStore {
  currentRow: Row<unknown> | undefined = undefined;
  selectedRows: SingleItem[] = [];
  selectedRowIndexes: string[] = [];
  selectAllChecked: boolean = false;
  selectedStartIndex: number | null = null;
  keyPressed: string | null = null;
  hoveredRow: Row<unknown> | null = null;
  isConfirmationVisible: boolean = false;
  resolvePromise: ((value: boolean) => void) | null = null;
  openProductDetailsPopup: OpenProductDetailsPopup = { open: false };
  openedObjectDetails: boolean = false;
  lastShiftSelectKey: string | undefined = undefined;
  fileMappingInProgress: FileMappingInProgress = {
    fileID: "",
    state: false,
  };
  matchForSelectedItems: boolean = false;
  open3DViewer: boolean = false;
  allObjectsIfcIDs: string[] = [];
  selectedGroupsGlobalIDs: string[] = [];
  hoveredGroupsGlobalIDs: string[] = [];
  fileCalculationsStatus: boolean = false;
  openBulkActionsMenu: boolean = false;
  errorMessage = "";
  fileMappingStatus: FileMappingStatusType | undefined;
  mappingPerformed = false;
  objectDetail: ObjectDetails = { loading: true, data: undefined };
  lastDraggedObject: LastDraggedObject = {
    index: undefined,
    direction: undefined,
  };
  currentFileID: string | undefined = undefined;
  openUpdateMappingsModal: boolean = false;
  outdatedComponents: OutdatedComponents = {
    open: false,
    count: 0,
    items: [],
  };
  openEditObjectPopup: OpenEditObjectPopup = {
    state: false,
    columnKey: undefined,
    updateKey: undefined,
    type: undefined,
  };
  isUndoInProgress: boolean = false;

  setCurrentRow(currentRow: Row<unknown> | undefined) {
    this.currentRow = currentRow;
  }

  showConfirmation(): Promise<boolean> {
    this.isConfirmationVisible = true;

    return new Promise<boolean>((resolve) => {
      this.resolvePromise = resolve;
    });
  }

  confirmMappingToFile() {
    this.isConfirmationVisible = false;
    this.resolvePromise?.(true);
    this.resolvePromise = null;
  }

  cancelMappingToFile() {
    this.isConfirmationVisible = false;
    this.resolvePromise?.(false);
    this.resolvePromise = null;
  }

  setSelectedRows(selectedRowsIndexes: string[]) {
    this.selectedRowIndexes = selectedRowsIndexes;
    this.selectedRows = selectedRowsIndexes.map((index) => {
      return dynamicTableStore.data.items[Number(index)];
    });
  }

  setSelectedStartIndex(selectedStartIndex: number | null) {
    this.selectedStartIndex = selectedStartIndex;
  }

  setHoveredRow(hoveredRow: Row<unknown> | null) {
    this.hoveredRow = hoveredRow;
  }

  addToSelectedRows(row_index: string[]) {
    this.setSelectedRows([...this.selectedRowIndexes, ...row_index]);
  }

  removeFromSelectedRows(row_index: string) {
    this.setSelectAllChecked(false);
    const filteredItems = this.selectedRowIndexes.filter(
      (item) => item !== row_index
    );
    this.setSelectedRows(filteredItems);
  }

  setSelectAllChecked(selectAllChecked: boolean) {
    this.selectAllChecked = selectAllChecked;
    if (!selectAllChecked) this.setSelectedStartIndex(null);
  }

  setKeyPressed(keyPressed: string | null) {
    this.keyPressed = keyPressed;
  }

  setLastShiftSelectKey(lastShiftSelectKey: string | undefined) {
    this.lastShiftSelectKey = lastShiftSelectKey;
  }

  updateSelectedRows() {
    if (dynamicTableStore.data.items.length < this.selectedRowIndexes.length) {
      this.selectedRowIndexes.splice(dynamicTableStore.data.items.length);
      this.setSelectedRows(this.selectedRowIndexes);
    }
  }

  setOpenProductDetailsPopup(openProductDetailsPopup: OpenProductDetailsPopup) {
    this.openProductDetailsPopup = openProductDetailsPopup;
  }

  setOpenedObjectDetails(openedObjectDetails: boolean) {
    this.openedObjectDetails = openedObjectDetails;
  }

  setOpenUpdateMappingsModal(openUpdateMappingsModal: boolean) {
    this.openUpdateMappingsModal = openUpdateMappingsModal;
  }

  setOpenOutdatedComponentsPopup(
    openOutdatedComponentsPopUp: OutdatedComponents
  ) {
    this.outdatedComponents = openOutdatedComponentsPopUp;
  }

  setOpenEditObjectPopup(openEditObjectPopup: OpenEditObjectPopup) {
    this.openEditObjectPopup = openEditObjectPopup;
  }

  closeEditObjectPopup() {
    this.openEditObjectPopup = {
      state: false,
      columnKey: undefined,
      updateKey: undefined,
      type: undefined,
    };
  }

  setFileMappingInProgress(fileMappingInProgress: FileMappingInProgress) {
    this.fileMappingInProgress = fileMappingInProgress;
  }

  setMatchForSelectedItems(matchForSelectedItems: boolean) {
    this.matchForSelectedItems = matchForSelectedItems;
  }

  setOpen3DViewer(open3DViewer: boolean) {
    this.open3DViewer = open3DViewer;
  }

  setSelectedGroupsGlobalIDs(selectedGroupsGlobalIDs: string[]) {
    this.selectedGroupsGlobalIDs = selectedGroupsGlobalIDs;
  }

  setHoveredGroupsGlobalIDs(hoveredGroupsGlobalIDs: string[]) {
    this.hoveredGroupsGlobalIDs = hoveredGroupsGlobalIDs;
  }

  setFileCalculationsStatus(fileCalculationsStatus: boolean) {
    this.fileCalculationsStatus = fileCalculationsStatus;
  }

  setOpenBulkActionsMenu(openBulkActionsMenu: boolean) {
    this.openBulkActionsMenu = openBulkActionsMenu;
  }

  setMappingPerformed(performed: boolean) {
    this.mappingPerformed = performed;
  }

  setFileMappingStatus(fileMappingStatus: FileMappingStatusType) {
    this.fileMappingStatus = fileMappingStatus;
  }

  setErrorMessage(message: string) {
    this.errorMessage = message;
  }

  clearErrorMessage() {
    this.errorMessage = "";
  }

  setObjectDetail(objectDetail: ObjectDetails) {
    this.objectDetail = objectDetail;
  }

  setLastDraggedObject(lastDraggedObject: LastDraggedObject) {
    this.lastDraggedObject = lastDraggedObject;
  }

  setCurrentFileID(currentFileID: string | undefined) {
    this.currentFileID = currentFileID;
  }

  setIsUndoInProgress(isUndoInProgress: boolean) {
    this.isUndoInProgress = isUndoInProgress;
  }

  getCurrentRowObject() {
    const objectID = (MEStore.currentRow?.original as SingleItem)?.id;
    if (objectID)
      return dynamicTableStore.data.items.find((item) => item.id === objectID);
  }

  get interactionDisabled(): boolean {
    return (
      filterMEStore.openFilter ||
      filterMEStore.openSort ||
      dynamicTableStore.openColumnSettings.open ||
      dynamicTableStore.openAddColumns ||
      this.openBulkActionsMenu
    );
  }

  private updateObjects(
    updatedObjects: SingleItem[],
    operation_type: OperationType
  ) {
    let _items = [...dynamicTableStore.data.items];

    if (operation_type === OperationType.IgnoreObjects) {
      const idsToRemove = new Set<string>(
        updatedObjects.map((e) => e.id ?? "")
      );
      _items = _items.filter((rec) => !idsToRemove.has(rec.id ?? ""));
    } else {
      updatedObjects.forEach((rec) => {
        const foundedIndex = _items.findIndex((item) => item.id === rec?.id);
        _items[foundedIndex] = { ...rec };
      });
    }

    dynamicTableStore.setData({
      count: dynamicTableStore.data.count,
      items: _items,
    });
  }

  private updateGroups(updatedGroups: SingleItem[]) {
    const groupKeys = filterMEStore.validGroupByRules.map(
      (item) => item.column_key
    );
    const _items = [...dynamicTableStore.data.items];
    updatedGroups.forEach((updatedItem) => {
      const foundedIndex = _items.findIndex((item) =>
        isTheSameItem(item, updatedItem)
      );
      _items[foundedIndex] = { ...updatedItem };
    });
    dynamicTableStore.setData({
      count: dynamicTableStore.data.count,
      items: _items,
    });

    function isTheSameItem(item: SingleItem, updatedItem: SingleItem) {
      return (
        groupKeys
          .map(
            (key) =>
              item.values[key as keyof typeof item.values] ===
              updatedItem.values[key as keyof typeof item.values]
          )
          .filter(Boolean).length === groupKeys.length
      );
    }
  }

  isCurrentFileMappingInProgress(file_id?: string) {
    return (
      this.fileMappingInProgress.fileID === (file_id ?? this.currentFileID) &&
      this.fileMappingInProgress.state
    );
  }

  resetData() {
    dynamicTableStore.resetTableData();
    this.selectAllChecked = false;
    this.selectedRows = [];
    this.selectedRowIndexes = [];
    this.allObjectsIfcIDs = [];
    filterMEStore.reset();
    this.openedObjectDetails = false;
    this.open3DViewer = false;
    this.fileCalculationsStatus = false;
    this.openBulkActionsMenu = false;
  }

  // API calls

  async refreshTableObjects() {
    await dynamicTableStore.fetchObjectsList(0, true);
    this.setSelectedRows([]);
  }

  async fetchObjectsList(props: ObjectListIN, page: number, pageSize: number) {
    if (!this.currentFileID) return;

    const authHeader = getLocalAuthHeader();
    const result = await mappingEditorObjectsApi
      .mappingEditorApiV1RoutersObjectsListObjectsByFileId(
        this.currentFileID,
        props,
        page,
        pageSize,
        authHeader
      )
      .then((response) => response.data)
      .catch((error) => {
        dynamicTableStore.setErrorFetchingObjects(true);
        console.error("error.ObjectsByFileId", error);
      });

    this.updateSelectedRows();
    await this.getAllObjectsIDs(props.filtering);
    return result;
  }

  private readonly onDoneMatching = async () => {
    await this.getObjectsMappingStatus(this.currentFileID);
    this.getFileCalculationsStatus();
    this.setFileMappingInProgress({ fileID: "", state: false });
    if (this.currentFileID) {
      mappingSnapshotStore.listMappingSnapshots(this.currentFileID);
    }
  };

  async runFileOperation(
    toast: (message: string, type: NotificationType) => void,
    file_id: string | undefined,
    operation_type: OperationType,
    product_id?: string | null,
    object_update_data?: ObjectUpdateData | null,
    confirm_all?: boolean
  ) {
    if (!file_id) return;
    const authHeader = getLocalAuthHeader();
    this.setFileMappingInProgress({ fileID: file_id, state: true });
    const props: FileOperationIN = {
      operation_type: operation_type,
      columns_to_display: dynamicTableStore.getColumnsProps(),
      confirm_all: Boolean(confirm_all),
    };
    if (filterMEStore.validFilterRules)
      props.filtering = filterMEStore.validFilterRules;
    if (filterMEStore.validSortingRules)
      props.sorting = filterMEStore.validSortingRules;
    if (product_id) props.product_uuid = product_id;
    if (object_update_data) props.object_update_data = object_update_data;
    return await fileOperationsApi
      .mappingEditorApiV1RoutersFileOperationsRunFileOperations(
        file_id,
        props,
        0,
        100,
        authHeader
      )
      .then((response) => {
        this.updateObjects(response.data.items, operation_type);
        this.setSelectedRows(this.selectedRowIndexes);
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error, "error.FileOperationsRunFileOperations");
        toast(error.message, "warning");
      })
      .finally(() => this.onDoneMatching());
  }

  async runSelectedObjectOperations(
    toast: (message: string, type: NotificationType) => void,
    file_id: string | undefined,
    operation_type: OperationType,
    produc_id?: string | null,
    current_object?: SingleItem | null,
    object_update_data?: ObjectUpdateData | null
  ) {
    if (!file_id) return;
    this.setFileMappingInProgress({ fileID: file_id, state: true });
    const authHeader = getLocalAuthHeader();

    const props: ObjectOperationIN = {
      operation_type: operation_type,
      columns_to_display: dynamicTableStore.getColumnsProps(),
      product_uuid: produc_id,
      filtering: filterMEStore.validFilterRules,
      sorting: filterMEStore.validSortingRules ?? [],
      grouping: filterMEStore.validGroupByRules,
    };

    if (filterMEStore.validGroupByRules.length) {
      props.group_selection = current_object
        ? filterMEStore.createCurrentRowGroupSelection(current_object)
        : filterMEStore.createSelectedRowsGroupSelection();
    } else {
      props.object_ids = current_object
        ? [String(current_object.id)]
        : this.selectedRows.map((item) => String(item?.id));
    }
    if (object_update_data) props.object_update_data = object_update_data;

    return await objectOperationsApi
      .mappingEditorApiV1RoutersObjectOperationsRunFileObjectOperations(
        file_id,
        props,
        authHeader
      )
      .then(async (response: AxiosResponse<ObjectListOUT>) => {
        filterMEStore.validGroupByRules.length
          ? await this.checkGroupsForUpdate(response)
          : this.updateObjects(response.data.items, operation_type);

        const selection =
          operation_type === OperationType.IgnoreObjects
            ? []
            : this.selectedRowIndexes;
        this.setSelectedRows(selection);
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error, "ObjectOperationsRunFileObjectOperations");
        toast(error.message, "warning");
      })
      .finally(() => this.onDoneMatching());
  }

  private async checkGroupsForUpdate(response: AxiosResponse<ObjectListOUT>) {
    filterMEStore.validGroupByRules.find(
      (rec) => rec.column_key === "match_calculations.product_name"
    )
      ? await dynamicTableStore.fetchObjectsList(0, true)
      : this.updateGroups(response.data.items);

    if (
      dynamicTableStore.visibleColumnProperties.some(
        (rec) => rec.match_dependant
      )
    ) {
      await dynamicTableStore.fetchObjectsList(0, true);
    }
  }

  async getAllObjectsIDs(filtering?: Filtering | null) {
    const authHeader = getLocalAuthHeader();
    if (!this.currentFileID) return;
    await mappingEditorObjectsApi
      .mappingEditorApiV1RoutersObjectsGetAllObjects(
        this.currentFileID,
        { filtering: filtering },
        authHeader
      )
      .then(
        (response) =>
          (this.allObjectsIfcIDs = response.data.map(
            (item) => item.ifc_global_id as string
          ))
      )
      .catch((error) => console.error(error, "error.GetAllObjects"));
  }

  async detailViewMapping(product_id: string | null) {
    bimViewerStore.currentObject?.id;
    const currentRowObjectID = bimViewerStore.openDetailsView
      ? bimViewerStore.objectDetail?.[0].id
      : this.getCurrentRowObject()?.id;

    if (!currentRowObjectID) return;
    const authHeader = getLocalAuthHeader();
    const operation: OperationType = product_id
      ? OperationType.ManualMatch
      : OperationType.UndoMatch;
    await objectOperationsApi
      .mappingEditorApiV1RoutersObjectOperationsDetailViewMapping(
        currentRowObjectID,
        {
          columns_to_display: dynamicTableStore.getColumnsProps(),
          product_uuid: product_id,
          operation_type: operation,
          detail_view_report_keys: {
            info: true,
            reports: true,
            ifc_properties: false,
          },
        } as DetailViewMappingIN,
        authHeader
      )
      .then((response) => {
        this.updateObjects([response.data.row_data], operation);
        detailViewV2Store.setInfoTileData(response.data.object_details.info);
        detailViewV2Store.setReportsData(
          product_id ? response.data.object_details.reports : null
        );
      })
      .catch((error) =>
        console.error(error, "ObjectOperationsDetailViewMapping")
      );
  }

  async fetchGroupsGlobalIfcIds(
    file_id: string | undefined,
    current_object?: SingleItem,
    hovered?: boolean
  ) {
    if (!file_id) return;
    const authHeader = getLocalAuthHeader();
    return await mappingEditorObjectsApi
      .mappingEditorApiV1RoutersObjectsFetchGlobalIfcIdsFromGroups(
        file_id,
        {
          group_selection: current_object
            ? filterMEStore.createCurrentRowGroupSelection(current_object)
            : filterMEStore.createSelectedRowsGroupSelection(),
          filtering: filterMEStore.validFilterRules,
        },
        authHeader
      )
      .then((response) => {
        const data = response.data;
        const ifcIDs = data
          .map((item) => item.ifc_global_id as string)
          .filter(Boolean);
        if (hovered) {
          this.setHoveredGroupsGlobalIDs(ifcIDs);
        } else {
          this.setSelectedGroupsGlobalIDs(ifcIDs);
        }
      })
      .catch((error) =>
        console.error(error, "error.ObjectsFetchGlobalIfcIdsFromGroups")
      );
  }

  async getFileCalculationsStatus() {
    if (!this.currentFileID) return;
    const authHeader = getLocalAuthHeader();
    return await calculationsApi
      .concularApiV1RoutersCalculationsGetCalculationsStatus(
        "file",
        this.currentFileID,
        authHeader
      )
      .then((response) =>
        this.setFileCalculationsStatus(response.data.status === "IN_PROGRESS")
      );
  }

  async getObjectsMappingStatus(ifc_id: string | undefined) {
    if (!ifc_id) return;
    this.clearErrorMessage();
    const authHeader = getLocalAuthHeader();
    this.setFileMappingStatus(undefined);
    await inventoryMappingEditorAPI
      .inventoryApiV1RoutersMappingEditorGetFileStatus(ifc_id, authHeader)
      .then((response: AxiosResponse) => {
        this.setFileMappingStatus(response.data);
      })
      .catch((error) => {
        console.error(error, "error.getObjectsMappingStatus");
        this.setErrorMessage((error as Error).message);
      });
  }

  async updateFileMappingStatus(ifcFile: MaterialResourceOUT | undefined) {
    ifcFile?.id
      ? await this.getObjectsMappingStatus(ifcFile?.id)
      : this.setFileMappingStatus(undefined);
  }

  async fetchColumnSuggestions(property: PropertyBrowserTreeNode) {
    if (!this.currentFileID || !property) return;
    return await dynamicMappingEditorFp()
      .mappingEditorApiV1RoutersIndexFilterRetrieveColumnSuggestions(
        this.currentFileID,
        {
          column_key: property.column_key,
          column_source: property.column_source as ColumnSourceEnum,
        }
      )
      .then((response) => response.data)
      .catch((error) =>
        console.error("error.RetrieveColumnSuggestions", error)
      );
  }

  async fetchColumns(): Promise<DynamicColumnOUT[] | void> {
    if (!this.currentFileID) return;
    return await mappingEditorDynamicColumnsFp()
      .mappingEditorApiV1RoutersDynamicColumnsGetDynamicColumns(
        this.currentFileID
      )
      .then((response) => response.data)
      .catch((error) => console.error("error.GetDynamicColumns", error));
  }

  async getProperties() {
    if (!this.currentFileID) return;
    return await dynamicMappingEditorFp()
      .mappingEditorApiV1RoutersIndexFilterPropertyBrowserFull(
        this.currentFileID,
        MappingEditorApiV1RoutersIndexFilterPropertyBrowserBuildingViewNameEnum.Dme
      )
      .then((response) => response.data)
      .catch((error) =>
        console.error("error.FilterPropertyBrowserFull", error)
      );
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: MappingEditorStore;

  static getInstance(): MappingEditorStore {
    if (!MappingEditorStore.instance) {
      MappingEditorStore.instance = new MappingEditorStore();
    }
    return MappingEditorStore.instance;
  }
}

export const MEStore = MappingEditorStore.getInstance();
